// src/Pages/Creator/Bio/CreatorBio.js
import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import API from "../../../../API";
import AlertDialog from "../../../../Components/AlertDialog";
import useAlertDialog from "../../../../Components/useAlertDialog";
import { BioPageContainer } from "./previewhelper";
import UploadFileDialogPFP from "../../../onboardupload";
import { styled } from "@mui/material/styles";
import ColorPickerGrid from "./colorpickergrid";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";   // NEW

// ************************************************************
// STYLED COMPONENTS
// ************************************************************
const ActionCard = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  cursor: "pointer",
  boxShadow: theme.shadows[3],
  "&:hover .itemOverlay": {
    opacity: 1,
  },
}));

const ItemMedia = styled("div")({
  width: "100%",
  height: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Overlay = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "rgba(0,0,0,0.4)",
  backdropFilter: "blur(6px)",
  opacity: 0,
  transition: "opacity 0.3s ease-in-out",
  textAlign: "center",
  padding: theme.spacing(2),
  color: "#fff",
}));

// ************************************************************
// REFACTORED COMPONENT
// ************************************************************
export default function CreatorBio() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  // Grab creator token so we can default slug to the username
  const { creatorToken } = useCreatorAuth();
  if (!creatorToken) return null; // wait for auth

  // ---------------------
  // 1. Hook: Fetch pages
  // ---------------------
  const {
    data: pages,
    isLoading: isPagesLoading,
    error: pagesError,
  } = useQuery(["creatorBioPages"], API.bio.list, {
    refetchOnWindowFocus: false,
  });

  // ---------------------
  // 2. Hooks: Local states
  // ---------------------
  const [isNewPage, setIsNewPage] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(-1);

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [qrEnabled, setQrEnabled] = useState(false);

  const [headerMediaUrl, setHeaderMediaUrl] = useState("");

  const [themeObj, setThemeObj] = useState({
    backgroundColor: "",
    foregroundColor: "",
  });

  // New state for color picker dialogs
  const [isBackgroundPickerOpen, setIsBackgroundPickerOpen] = useState(false);
  const [isForegroundPickerOpen, setIsForegroundPickerOpen] = useState(false);

  const [items, setItems] = useState([]);

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ---------------------
  // 3. Hooks: Mutations
  // ---------------------
  const createMutation = useMutation(
    (newPageData) => API.bio.create(newPageData),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page created!", () => {
          closeDialog();
          queryClient.invalidateQueries(["creatorBioPages"]);
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog
        );
      },
    }
  );

  const updateMutation = useMutation(
    ({ oldSlug, updated }) => API.bio.update(oldSlug, updated),
    {
      onSuccess: () => {
        openDialog("Success", "Bio page updated!", () => {
          closeDialog();
          queryClient.invalidateQueries(["creatorBioPages"]);
        });
      },
      onError: (error) => {
        openDialog(
          "Error",
          error?.response?.data?.error || error.message,
          closeDialog,
          closeDialog
        );
      },
    }
  );

  // ---------------------
  // 4. Early return checks
  // ---------------------
  if (isPagesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <Typography>Loading your bio pages...</Typography>
      </Box>
    );
  }

  if (pagesError) {
    return (
      <Box p={4}>
        <Typography color="error">
          Error loading bio pages:{" "}
          {pagesError?.response?.data?.error || pagesError.message}
        </Typography>
      </Box>
    );
  }

  // -----------------------------------------------------------
  // 5. Functions for events
  // -----------------------------------------------------------
  function handleSelectPage(index) {
    if (index === -1) {
      // ---- NEW PAGE ----
      setIsNewPage(true);
      setSelectedPageIndex(-1);

      // Default slug to creator's username
      setSlug(creatorToken.creator_user.username || "");

      setTitle("");
      setDescription("");
      setIsPublic(true);
      setQrEnabled(false);

      setHeaderMediaUrl("");
      setThemeObj({ backgroundColor: "", foregroundColor: "" });
      setItems([]);
    } else {
      // ---- EXISTING PAGE ----
      setIsNewPage(false);
      setSelectedPageIndex(index);
      const page = pages[index];

      setSlug(page.slug);
      setTitle(page.title || "");
      setDescription(page.description || "");
      setIsPublic(page.is_public);
      setThemeObj(page.theme || {});
      setQrEnabled(page.qr_enabled);

      setHeaderMediaUrl(page.header_media_url || "");
      setItems(page.items || []);
    }
  }

  function handleAddItem() {
    const newItem = {
      component_type: "LINK",
      label: "New Link",
      url: "https://example.com",
      media_url: "",
      size: "MEDIUM",
      is_enabled: true,
    };
    setItems((prev) => [...prev, newItem]);
  }

  function handleRemoveItem(idx) {
    const updated = [...items];
    updated.splice(idx, 1);
    setItems(updated);
  }

  function handleUpdateItemField(idx, field, value) {
    setItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  }

  function handleSave() {
    if (!slug) {
      openDialog(
        "Validation Error",
        "A slug is required.",
        closeDialog,
        closeDialog
      );
      return;
    }
    const payload = {
      slug,
      title,
      description,
      is_public: isPublic,
      qr_enabled: qrEnabled,

      theme: themeObj,
      header_media_url: headerMediaUrl,
      items: items.map((item, idx) => ({ ...item, order_index: idx })),
    };
    if (isNewPage) {
      createMutation.mutate(payload);
    } else {
      const oldSlug = pages[selectedPageIndex].slug;
      updateMutation.mutate({ oldSlug, updated: payload });
    }
  }

  function handleOpenUploadDialog(target) {
    setUploadTarget(target);
    setIsUploadDialogOpen(true);
  }

  function handleCloseUploadDialog(uploadedUrl) {
    setIsUploadDialogOpen(false);
    if (!uploadedUrl) return;
    if (uploadTarget === "header") {
      setHeaderMediaUrl(uploadedUrl);
    } else {
      const idx = uploadTarget;
      if (typeof idx === "number") {
        handleUpdateItemField(idx, "media_url", uploadedUrl);
      }
    }
  }

  // -------------------------
  // Drag & Drop handlers
  // -------------------------
  function onDragEnd(result) {
    if (!result.destination) return; // dropped outside
    const { source, destination } = result;
    if (source.index === destination.index) return; // same place

    setItems((prev) => {
      const updatedItems = [...prev];
      // remove from source
      const [moved] = updatedItems.splice(source.index, 1);
      // insert at destination
      updatedItems.splice(destination.index, 0, moved);
      return updatedItems;
    });
  }

  // -------------------------
  // Helper for rendering items in the preview
  // -------------------------
  function renderPreviewItem(item, idx) {
    return (
      <ActionCard
        key={`preview-${idx}`}
        style={{ marginBottom: "16px" }}
        onClick={() => {
          if (item.url) window.open(item.url, "_blank");
        }}
      >
        <ItemMedia>
          {/* If there's media (image or video) */}
          {item.media_url ? (
            /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.media_url) ? (
              <Box
                component="img"
                src={item.media_url}
                alt={item.label || "Item image"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <video
                src={item.media_url}
                autoPlay
                loop
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            )
          ) : (
            <Box
              sx={{
                width: "100%",
                height: 200,
                bgcolor: "grey.300",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="overline">No Media</Typography>
            </Box>
          )}
        </ItemMedia>

        {/* Blurred overlay for title/description */}
        <Overlay className="itemOverlay">
          {item.label && <Typography variant="h6">{item.label}</Typography>}
          {item.description && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {item.description}
            </Typography>
          )}
        </Overlay>
      </ActionCard>
    );
  }

  // -------------------------
  // Derived data
  // -------------------------
  const isHeaderImage =
    headerMediaUrl && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(headerMediaUrl);

  // sorted by drag‑n‑drop order
  const sortedItems = items;

  // -------------------------
  // RENDER
  // -------------------------
  return (
    <Box sx={{ p: 2 }}>
      <AlertDialog alertState={dialogState} />

      <Typography variant="h4" sx={{ mb: 2 }}>
        Manage Your Bio Pages
      </Typography>

      {/* Page selection */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {pages?.map((p, i) => (
          <Button
            key={p.id}
            variant={selectedPageIndex === i ? "contained" : "outlined"}
            onClick={() => handleSelectPage(i)}
          >
            {p.slug}
          </Button>
        ))}
        <Button
          variant={selectedPageIndex === -1 ? "contained" : "outlined"}
          onClick={() => handleSelectPage(-1)}
        >
          + Create New
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* LAYOUT: LEFT (page details) / RIGHT (items & preview) */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {/* LEFT: Page form */}
        <Paper sx={{ p: 2, minWidth: 300, flex: "1 1 300px" }} elevation={3}>
          <Typography variant="h6" gutterBottom>
            {isNewPage ? "Create New Bio Page" : "Edit Bio Page"}
          </Typography>

          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            margin="normal"
            disabled={!isNewPage}
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />

          <FormControlLabel
            control={
              <Switch
                checked={isPublic}
                onChange={e => setIsPublic(e.target.checked)}
              />
            }
            label="Is Public?"
          />

          {/* ADD THIS: */}
          <FormControlLabel
            control={
              <Switch
                checked={qrEnabled}
                onChange={e => setQrEnabled(e.target.checked)}
              />
            }
            label="Enable QR Code"
          />

          {/* Header Media */}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Header Media
          </Typography>
          <TextField
            label="Header Media URL"
            value={headerMediaUrl}
            onChange={(e) => setHeaderMediaUrl(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Button
            variant="outlined"
            onClick={() => handleOpenUploadDialog("header")}
          >
            Upload Header Media
          </Button>

          {/* Theme Settings with Color Pickers */}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Theme Settings
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setIsBackgroundPickerOpen(true)}
            >
              Background: {themeObj.backgroundColor || "#FFFFFF"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setIsForegroundPickerOpen(true)}
            >
              Foreground: {themeObj.foregroundColor || "#000000"}
            </Button>
          </Box>

          <Button variant="contained" onClick={handleSave} sx={{ mt: 3 }}>
            Save
          </Button>
        </Paper>

        {/* RIGHT: Items list & preview */}
        <Box
          sx={{ flex: 1, minWidth: 300, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {isMobile && (
            <Button variant="contained" onClick={() => setIsPreviewOpen(true)}>
              Show Preview
            </Button>
          )}

          <Paper sx={{ p: 2, flex: 1 }} elevation={3}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Items
            </Typography>
            <Button variant="outlined" onClick={handleAddItem} sx={{ mb: 1 }}>
              + Add Item
            </Button>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="itemsDroppable">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {sortedItems.map((it, idx) => (
                      <Draggable
                        key={`item-${idx}`}
                        draggableId={`item-${idx}`}
                        index={idx}
                      >
                        {(provided) => (
                          <Accordion
                            ref={provided.innerRef}
                            sx={{ mb: 1 }}
                            {...provided.draggableProps}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              {...provided.dragHandleProps}
                            >
                              <Typography>
                                {it.label || "(Untitled)"} – {it.size}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <TextField
                                  label="Label"
                                  size="small"
                                  value={it.label}
                                  onChange={(e) =>
                                    handleUpdateItemField(
                                      idx,
                                      "label",
                                      e.target.value
                                    )
                                  }
                                />
                                <TextField
                                  label="URL"
                                  size="small"
                                  value={it.url || ""}
                                  onChange={(e) =>
                                    handleUpdateItemField(
                                      idx,
                                      "url",
                                      e.target.value
                                    )
                                  }
                                />
                                <TextField
                                  label="Description"
                                  size="small"
                                  multiline
                                  minRows={2}
                                  value={it.description || ""}
                                  onChange={(e) =>
                                    handleUpdateItemField(
                                      idx,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                />
                                <TextField
                                  label="Media URL"
                                  size="small"
                                  value={it.media_url || ""}
                                  onChange={(e) =>
                                    handleUpdateItemField(
                                      idx,
                                      "media_url",
                                      e.target.value
                                    )
                                  }
                                />
                                <Button
                                  variant="outlined"
                                  onClick={() => handleOpenUploadDialog(idx)}
                                >
                                  Upload Media
                                </Button>

                                <FormControl size="small" sx={{ width: 120, mt: 1 }}>
                                  <InputLabel>Size</InputLabel>
                                  <Select
                                    value={it.size || "MEDIUM"}
                                    label="Size"
                                    onChange={(e) =>
                                      handleUpdateItemField(idx, "size", e.target.value)
                                    }
                                  >
                                    <MenuItem value="SMALL">SMALL</MenuItem>
                                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                                    <MenuItem value="LARGE">LARGE</MenuItem>
                                  </Select>
                                </FormControl>

                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                  <IconButton
                                    onClick={() => handleRemoveItem(idx)}
                                    sx={{ color: "red" }}
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Paper>

          {!isMobile && (
            <Paper sx={{ p: 2 }} elevation={1}>
              <Typography variant="h6" gutterBottom>
                Live Preview
              </Typography>
              <BioPageContainer customtheme={themeObj}>
                {headerMediaUrl && (
                  <Box sx={{ mb: 2 }}>
                    {isHeaderImage ? (
                      <Box
                        component="img"
                        src={headerMediaUrl}
                        alt="Header Media"
                        sx={{
                          width: "100%",
                          maxHeight: 300,
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <video
                        src={headerMediaUrl}
                        controls
                        style={{ width: "100%", maxHeight: 300 }}
                      />
                    )}
                  </Box>
                )}

                {!isHeaderImage && headerMediaUrl && (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <em>(Header video shown above.)</em>
                  </Typography>
                )}

                <Box sx={{ p: 2, width: "100%" }}>
                  <Typography variant="h4" textAlign="center" gutterBottom>
                    {title || slug || "Untitled"}
                  </Typography>
                  <Typography variant="body1" textAlign="center">
                    {description}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2, width: "100%" }} />

                <Box sx={{ width: "100%", maxWidth: 600 }}>
                  {sortedItems.map(renderPreviewItem)}
                </Box>
              </BioPageContainer>
            </Paper>
          )}
        </Box>
      </Box>

      {/* MOBILE PREVIEW DIALOG */}
      <Dialog fullScreen open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
        <Box sx={{ p: 2 }}>
          <Button onClick={() => setIsPreviewOpen(false)}>Close Preview</Button>
        </Box>
        <Box sx={{ p: 2 }}>
          <BioPageContainer customtheme={themeObj}>
            {headerMediaUrl && (
              <Box sx={{ mb: 2 }}>
                {isHeaderImage ? (
                  <Box
                    component="img"
                    src={headerMediaUrl}
                    alt="Header Media"
                    sx={{
                      width: "100%",
                      maxHeight: 300,
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <video
                    src={headerMediaUrl}
                    controls
                    style={{ width: "100%", maxHeight: 300 }}
                  />
                )}
              </Box>
            )}

            {!isHeaderImage && headerMediaUrl && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <em>(Header video shown above.)</em>
              </Typography>
            )}

            <Box sx={{ p: 2, width: "100%" }}>
              <Typography variant="h4" textAlign="center" gutterBottom>
                {title || slug || "Untitled"}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {description}
              </Typography>
            </Box>
            <Divider sx={{ my: 2, width: "100%" }} />

            <Box sx={{ width: "100%", maxWidth: 600 }}>
              {sortedItems.map(renderPreviewItem)}
            </Box>
          </BioPageContainer>
        </Box>
      </Dialog>

      {/* File‑uploader dialog */}
      <UploadFileDialogPFP open={isUploadDialogOpen} onClose={handleCloseUploadDialog} />

      {/* Color Picker Dialogs */}
      <ColorPickerGrid
        open={isBackgroundPickerOpen}
        onClose={() => setIsBackgroundPickerOpen(false)}
        selectedColor={themeObj.backgroundColor || "#FFFFFF"}
        onColorSelect={(color) =>
          setThemeObj((prev) => ({ ...prev, backgroundColor: color }))
        }
      />
      <ColorPickerGrid
        open={isForegroundPickerOpen}
        onClose={() => setIsForegroundPickerOpen(false)}
        selectedColor={themeObj.foregroundColor || "#000000"}
        onColorSelect={(color) =>
          setThemeObj((prev) => ({ ...prev, foregroundColor: color }))
        }
      />
    </Box>
  );
}
